import * as React from 'react';
import { ELCLogger, TriggerType } from '@estee/elc-logging';
import { withWrapper, displayTypes } from './wrappers/withWrapper';
import { getServiceVersion } from '@estee/elc-universal-utils';

export type JSXElement = JSX.Element | React.ComponentType | null | false | undefined;
type JSXElementFunction = () => JSXElement;

export interface IConfigurableElement {
    component: JSXElementFunction;
    label?: string;
}

export interface IViewRegistry {
    [key: string]: JSXElementFunction | IConfigurableElement;
}

export interface IOrderConfigs {
    weight: number;
    elements: IOrderConfig;
    display?: displayTypes;
    data?: IOrderData;
    params?: IOrderParams;
}
export interface IOrderConfig {
    [key: string]: IOrderConfigs;
}

interface IOrderParams {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
}

interface IOrderData {
    [key: string]: string;
}
interface IOrderRenderProps {
    viewRegistry: IViewRegistry;
    orderConfig: IOrderConfig;
    serviceName: string;
}

interface IComponentData {
    component: React.ReactElement;
    label: string;
}

const getComponentData = (
    componentItem: JSXElementFunction | IConfigurableElement
): IComponentData => {
    let component;
    let label = '';

    if (
        typeof componentItem === 'object' &&
        componentItem &&
        (componentItem as IConfigurableElement).component
    ) {
        component = (componentItem as IConfigurableElement).component;
        label = componentItem.label || '';
    } else {
        component = componentItem;
    }

    if (typeof component === 'function') {
        component = component();
    }

    return { component: component as React.ReactElement, label };
};

// @TODO - Find a better way to handle render responsabilities
// Tech debt - MPPFIND-3406
const orderChildren = (
    viewRegistry: IViewRegistry,
    orderConfig: IOrderConfig,
    logger: ELCLogger
): JSXElement[] => {
    const orderedComponentNames = Object.keys(orderConfig).sort(
        (a, b) => orderConfig[a].weight - orderConfig[b].weight
    );

    return orderedComponentNames.map(componentName => {
        const isComponentAvailable = Object.keys(viewRegistry).includes(componentName);

        const componentConfig = orderConfig[componentName];

        if (!isComponentAvailable) {
            logger.error({
                message: `[elc-layouts]: Component '${componentName}' specified in Drupal Order Config, does not exist.`,
                triggerType: TriggerType.render,
                payload: {
                    context: {
                        viewRegistry
                    }
                }
            });
        }

        const { component, label } = getComponentData(viewRegistry[componentName]);

        if (!component) {
            return null;
        }

        const elements = componentConfig.elements;

        if (componentConfig.data) {
            // merge custom data set on the feature into the final componentConfig
            component.props.componentConfig = {
                ...component.props.componentConfig,
                ...componentConfig.data
            };
        }

        if (elements && Object.keys(elements).length) {
            let orderedChildren: JSXElement[] | Function = orderChildren(
                viewRegistry,
                elements,
                logger
            );

            if (typeof component.props.children === 'function') {
                orderedChildren = () => orderChildren(viewRegistry, elements, logger);
            }

            const returnElement = React.createElement(
                component.type,
                component.props,
                orderedChildren
            );

            return withWrapper(
                { ...returnElement, key: componentName },
                {
                    displayType: componentConfig.display,
                    label
                },
                logger
            );
        }

        return withWrapper(
            { ...component, key: componentName },
            {
                displayType: componentConfig.display,
                label
            },
            logger
        );
    });
};

export const OrderRender = (props: IOrderRenderProps) => {
    const { viewRegistry, orderConfig, serviceName } = props;
    const logger = new ELCLogger({ serviceName, serviceVersion: getServiceVersion(serviceName) });

    const fullElements = orderChildren(viewRegistry, orderConfig, logger);

    return <>{fullElements}</>;
};
